// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
// Constants
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { QueryParameterKeys, SubRouteKeys, SubRouteType, ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
// Models
import { extractMyPlansRoutingDetailsFromSubRoute, extractPlanRoutingDetailsFromSubRoute, fillSubRouteDefaults, generateSubRouteFromRoutingDetailsForMyPlans } from "@ms/uno-routing";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { Store } from "../Store";
// Utilities
import { getValueForKey, removeKeyFromSubRoute, serializeRouteDescriptor, setSubRouteKeyValue } from "@ms/uno-routing";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { setCurrentRouteInfoForLogs } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { stringOrdinalCaseInsensitiveCompare } from "@ms/uno-utilities/lib/local/Comparators";
import { isEqual } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import cloneDeep from "lodash/cloneDeep";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
/**
 * Routing Store for navigation purposes
 */ export class RouteStore extends EventPublishingStore {
    get name() {
        return Store.Route;
    }
    getCurrentPath() {
        return this.formRoutePath(this.currentRoute);
    }
    getCurrentSubRouteType() {
        return this.currentRoute.subRouteType;
    }
    getCurrentSubRoute() {
        return this.currentRoute.subRoute;
    }
    getCurrentViewId() {
        return this.generateCacheKey(this.currentRoute.subRouteType, this.currentRoute.subRoute);
    }
    getCurrentQueryParameters() {
        return this.currentRoute.queryParameters ?? {};
    }
    getCurrentViewType() {
        const subRouteType = this.getCurrentSubRouteType();
        const planRoutingDetails = extractPlanRoutingDetailsFromSubRoute(this.getCurrentSubRoute(), subRouteType);
        const currentPivot = planRoutingDetails?.viewType || ViewType.Board;
        return currentPivot;
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.NavigateToView:
                storeChanged = this.handleNavigateToView(action);
                break;
            case ActionType.PushSubRoute:
                storeChanged = this.handlePushSubRoute(action);
                break;
            case ActionType.ReplaceSubRoute:
                storeChanged = this.handleReplaceSubRoute(action);
                break;
            case ActionType.OpenModalAction:
                storeChanged = this.handleOpenModal(action);
                break;
            case ActionType.CloseModalAction:
                storeChanged = this.handleCloseModal(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                break;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    /**
     * Handles the navigate to view action
     * @param action the NavigateToViewAction action containing the navigation URL
     * @returns storeChanged - true if navigation route has changed, false otherwise
     */ handleNavigateToView(action) {
        const { routeChangeDescriptor } = action;
        const currentPath = this.formRoutePath(this.currentRoute);
        if (this.shouldUseCachedSubRoute(routeChangeDescriptor.subRouteType, routeChangeDescriptor.subRoute)) {
            const cacheKey = this.generateCacheKey(routeChangeDescriptor.subRouteType, routeChangeDescriptor.subRoute);
            const cachedSubRoute = this.subRouteCache[cacheKey] ?? [];
            // Use the cached sub route if available for the new route or else use the passed sub route
            this.currentRoute.subRoute = cachedSubRoute.length === 0 ? routeChangeDescriptor.subRoute : cachedSubRoute;
            // Bump to most-recently-used on read so that frequently-opened-but-pivot-unchanged plans are not
            // evicted ahead of rarely-opened plans whose pivot was changed more recently.
            if (cachedSubRoute.length > 0 && !this.configProvider().changeGates.DisablePlanSubroutePersistence) {
                delete this.subRouteCache[cacheKey];
                this.subRouteCache[cacheKey] = cachedSubRoute;
                this.persistSubRouteCache();
            }
        } else {
            this.currentRoute.subRoute = routeChangeDescriptor.subRoute;
        }
        // Fill in sub route defaults if needed
        this.currentRoute.subRoute = fillSubRouteDefaults(this.currentRoute.subRoute, routeChangeDescriptor.subRouteType);
        this.currentRoute.subRouteType = routeChangeDescriptor.subRouteType;
        this.currentRoute.queryParameters = routeChangeDescriptor.queryParameters;
        // Write through and persist to persistent storage if supported
        this.persistRoute();
        // Determine the new path and if there was any actual change
        const newPath = this.formRoutePath(this.currentRoute);
        if (stringOrdinalCaseInsensitiveCompare(currentPath, newPath) === 0) {
            return false;
        }
        // Push the new path to the browser history if there was a change, and we aren't skipping history push state
        if (!action.skipHistoryPushState) {
            history.pushState(null, "", newPath);
        }
        setCurrentRouteInfoForLogs(this.currentRoute.subRouteType, this.currentRoute.subRoute, this.loggers.logHandler);
        return true;
    }
    /**
     * Handles the push sub route action
     * @param action the PushSubRouteAction action containing the sub route
     * @returns storeChanged - true if the sub route changed, false otherwise
     */ handlePushSubRoute(action) {
        return this.changeSubRoute(action.subRoute, (newPath)=>{
            history.pushState(null, "", newPath);
        });
    }
    /**
     * Handles the replace sub route action
     * @param action the ReplaceSubRouteAction action containing the sub route
     * @returns storeChanged - true if the sub route changed, false otherwise
     */ handleReplaceSubRoute(action) {
        if (isEqual(this.currentRoute?.subRoute, action.subRoute)) {
            return false;
        }
        this.changeSubRoute(action.subRoute, (newPath)=>{
            history.replaceState(null, "", newPath);
        });
        return true;
    }
    /**
     * Change the sub route to a new one
     * @param newSubRoute New sub route
     * @param historyCallback History callback to call after the sub route has been changed
     * @returns Boolean indicating whether there was any actual store change
     */ changeSubRoute(newSubRoute, historyCallback) {
        if (isEqual(this.currentRoute?.subRoute, newSubRoute)) {
            return false;
        }
        this.currentRoute.subRoute = newSubRoute;
        const newPath = this.formRoutePath(this.currentRoute);
        if (newPath) {
            const cacheKey = this.generateCacheKey(this.currentRoute.subRouteType, newSubRoute);
            if (this.configProvider().changeGates.DisablePlanSubroutePersistence) {
                // Kill-switch: revert to the pre-fix behavior of writing the cache entry in place
                this.subRouteCache[cacheKey] = newSubRoute;
            } else {
                // Persist the sub route in cache, bumping it to the most-recently-used position for LRU ordering
                delete this.subRouteCache[cacheKey];
                this.subRouteCache[cacheKey] = newSubRoute;
            }
            this.persistSubRouteCache();
            historyCallback(newPath);
        }
        // Write through and persist to persistent storage if supported
        this.persistRoute();
        setCurrentRouteInfoForLogs(this.currentRoute.subRouteType, this.currentRoute.subRoute, this.loggers.logHandler);
        return true;
    }
    /**
     * Change the route based on the open modal
     */ handleOpenModal(action) {
        let storeChanged = false;
        if (action.modalData.modalType === ModalType.TaskEditor) {
            switch(this.currentRoute.subRouteType){
                case SubRouteType.MyTasks:
                case SubRouteType.MyDay:
                case SubRouteType.TodoList:
                case SubRouteType.BasicPlan:
                    // Change route and push to history so that user can navigate back to view
                    const newSubRoute = setSubRouteKeyValue(this.currentRoute.subRoute, SubRouteKeys.Task, action.modalData.taskId);
                    storeChanged = this.changeSubRoute(newSubRoute, (newPath)=>{
                        history.pushState(null, "", newPath);
                    });
                    break;
                default:
            }
        }
        return storeChanged;
    }
    /**
     * Change the route when a modal is closed
     */ handleCloseModal(action) {
        let storeChanged = false;
        let updateHistory = (newPath)=>{
            history.pushState(null, "", newPath);
        };
        if (action.modalData.modalType === ModalType.TaskEditor) {
            switch(this.currentRoute.subRouteType){
                case SubRouteType.MyTasks:
                case SubRouteType.MyDay:
                case SubRouteType.TodoList:
                case SubRouteType.BasicPlan:
                    // Change route and push to history so that user can navigate back to modal
                    const newSubRoute = removeKeyFromSubRoute(this.currentRoute.subRoute, SubRouteKeys.Task);
                    storeChanged = this.changeSubRoute(newSubRoute, (newPath)=>{
                    // Do nothing, history is pushed once below
                    });
                    break;
                default:
            }
        }
        if (action.modalData.modalType === ModalType.CreatePlan && this.currentRoute.subRouteType === SubRouteType.MyPlans) {
            const routingDetails = extractMyPlansRoutingDetailsFromSubRoute(this.currentRoute.subRoute);
            if (routingDetails?.dialogType === ModalType.CreatePlan) {
                delete routingDetails.dialogType;
                delete routingDetails.templateType;
                const newSubRoute = generateSubRouteFromRoutingDetailsForMyPlans(routingDetails);
                const subRouteChanged = this.changeSubRoute(newSubRoute, ()=>{
                // History is replaced once below with any other close-time route cleanup applied.
                });
                storeChanged = subRouteChanged || storeChanged;
                if (subRouteChanged) {
                    updateHistory = (newPath)=>{
                        history.replaceState(null, "", newPath);
                    };
                }
            }
        }
        // Remove the modalType from query parameters, if it exists and matches the modalType of the closed modal
        if (this.currentRoute.queryParameters?.[QueryParameterKeys.Modal] === action.modalData.modalType) {
            delete this.currentRoute.queryParameters[QueryParameterKeys.Modal];
            // Remove the templateType from query parameters, if it exists and modal was CreatePlan
            if (action.modalData.modalType === ModalType.CreatePlan && this.currentRoute.queryParameters?.[QueryParameterKeys.TemplateType]) {
                delete this.currentRoute.queryParameters[QueryParameterKeys.TemplateType];
            }
            storeChanged = true;
        }
        // If the store changed, update browser history & persist to storage.
        if (storeChanged) {
            updateHistory(this.formRoutePath(this.currentRoute));
            setCurrentRouteInfoForLogs(this.currentRoute.subRouteType, this.currentRoute.subRoute, this.loggers.logHandler);
            this.persistRoute();
        }
        return storeChanged;
    }
    /**
     * Forms the route path by serializing the route descriptor in the format of a URL
     * @param routeDescriptor Route descriptor
     */ formRoutePath(routeDescriptor) {
        let routeToSerialize = routeDescriptor;
        if (this.defaultQueryParameters) {
            const clonedRouteDescriptor = cloneDeep(routeDescriptor);
            const clonedQueryParameters = clonedRouteDescriptor.queryParameters ?? {};
            clonedRouteDescriptor.queryParameters = {
                ...this.defaultQueryParameters,
                ...clonedQueryParameters
            };
            routeToSerialize = clonedRouteDescriptor;
        }
        return serializeRouteDescriptor(routeToSerialize, this.routePathPrefix);
    }
    /**
     * Generate a cache key for the subroute cache
     * @param subRouteType Type of subroute
     * @param subRoute Subroute array
     */ generateCacheKey(subRouteType, subRoute) {
        if (subRouteType === SubRouteType.BasicPlan) {
            return `${subRouteType}${subRoute[0]}`;
        } else if (subRouteType === SubRouteType.PremiumPlan) {
            const orgId = getValueForKey(subRoute, SubRouteKeys.Org);
            return `${subRouteType}${subRoute[0]}${orgId}`; // ProjectId + OrgId
        } else if (subRouteType === SubRouteType.TodoList) {
            return `${subRouteType}${subRoute[0]}`;
        } else if (subRouteType === SubRouteType.Portfolio) {
            return `${subRouteType}${subRoute[0]}`;
        } else {
            return subRouteType;
        }
    }
    /**
     * When we navigateToView, we typically want to use the cached subroute (if it exists)
     * e.g., For "My Tasks", use the pivot that was last selected
     * However, if newSubRoute specifically wants to target a particular view, we want to adhere to it (and not use cached subRoute)
     * This is especially true for Basic Plan navigation (if we are deep linking to a Task, we want go to that view and not cached subRoute)
     */ shouldUseCachedSubRoute(newSubRouteType, newSubRoute) {
        switch(newSubRouteType){
            case SubRouteType.BasicPlan:
            case SubRouteType.TodoList:
                // More than 1 element (planId) means we have intended view (e.g., Task) so don't use cached subRoute
                return newSubRoute.length === 1;
            case SubRouteType.PremiumPlan:
                // More than 3 elements [<PlanId>, "Org", <OrgId>] means we have intended view (e.g., Task/View) so don't use cached subRoute
                return newSubRoute.length === 3;
            case SubRouteType.Portfolio:
                return newSubRoute.length === 1;
            case SubRouteType.MyPlans:
            case SubRouteType.MyTasks:
            case SubRouteType.Publishing:
                // Any element means we have intended view (e.g., AssignedToMe) so don't use cached subRoute
                return newSubRoute.length === 0;
            default:
                return true;
        }
    }
    /**
     * Persist the sub route cache to persistent storage.
     * When persistableSubRouteTypes is set, only entries whose key starts with an allowed type are persisted.
     * Persisted entries are bounded via least-recently-used eviction to prevent unbounded storage growth.
     */ persistSubRouteCache() {
        const isRoutePersistenceFeatureEnabled = this.configProvider().settings.routePersistenceEnabled;
        if (isRoutePersistenceFeatureEnabled && this.persistRoutes) {
            try {
                const persistableEntries = this.persistableSubRouteTypes == null ? Object.entries(this.subRouteCache) : Object.entries(this.subRouteCache).filter(([key])=>this.isKeyPersistable(key));
                // Kill-switch: LRU eviction only applies to the (gated) plan-persistence feature. When disabled,
                // fall back to the pre-fix behavior of persisting the filtered entries as-is.
                const boundedEntries = this.configProvider().changeGates.DisablePlanSubroutePersistence ? persistableEntries : this.evictLeastRecentlyUsed(persistableEntries);
                this.persistentStorage?.setItem(RouteStore.SubRouteCacheKey, JSON.stringify(Object.fromEntries(boundedEntries)));
            } catch  {
            // Best-effort persistence; failures are non-critical
            }
        }
    }
    /**
     * Enforce an upper bound on persisted sub-route cache entries using least-recently-used eviction.
     * Entries are ordered oldest-first (index 0 = least recently used); MyTasks entries are protected
     * and never evicted. The limit reuses the shared maxViewConfigEntries setting.
     */ evictLeastRecentlyUsed(entries) {
        const maxEntries = this.configProvider().settings.evoPersistenceSettings.maxViewConfigEntries;
        const boundedEntries = [
            ...entries
        ];
        while(boundedEntries.length > maxEntries){
            const indexToEvict = boundedEntries.findIndex(([key])=>!key.startsWith(SubRouteType.MyTasks));
            if (indexToEvict === -1) {
                break;
            }
            boundedEntries.splice(indexToEvict, 1);
        }
        return boundedEntries;
    }
    /** Returns true if a cache key belongs to a persistable sub route type */ isKeyPersistable(key) {
        // Plan (BasicPlan) subroute persistence is gated. When disabled, revert to the pre-fix behavior
        // where plan view pivots are neither persisted nor hydrated.
        if (key.startsWith(SubRouteType.BasicPlan) && this.configProvider().changeGates.DisablePlanSubroutePersistence) {
            return false;
        }
        if (this.persistableSubRouteTypes == null) {
            return true;
        }
        for (const type of this.persistableSubRouteTypes){
            if (key.startsWith(type)) {
                return true;
            }
        }
        return false;
    }
    /**
     * Hydrate the sub route cache from persistent storage.
     * When persistableSubRouteTypes is set, only matching entries are loaded.
     */ hydrateSubRouteCache() {
        try {
            const raw = this.persistentStorage?.getItem(RouteStore.SubRouteCacheKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                    const validatedEntries = Object.entries(parsed).filter(([, value])=>Array.isArray(value) && value.every((item)=>typeof item === "string"));
                    if (this.persistableSubRouteTypes == null) {
                        return Object.fromEntries(validatedEntries);
                    }
                    // Only hydrate entries for allowed types
                    return Object.fromEntries(validatedEntries.filter(([key])=>this.isKeyPersistable(key)));
                }
            }
        } catch  {
        // Ignore parse failures; start fresh
        }
        return {};
    }
    /**
     * Persist the current route to persistent storage if supported
     */ persistRoute() {
        const isRoutePersistenceFeatureEnabled = this.configProvider().settings.routePersistenceEnabled;
        if (isRoutePersistenceFeatureEnabled && this.persistRoutes) {
            try {
                this.persistentStorage?.setItem(RouteStore.RouteKey, JSON.stringify(this.currentRoute));
            } catch (e) {
                this.loggers.traceLogger.logTrace(0x1e1a17d6 /* tag_4g75w */ , TraceLevel.Warning, `Persistence failure. [Store=${this.name}][IsRoutePersistenceEnabled=${isRoutePersistenceFeatureEnabled}][ShouldPersist=${this.persistRoutes}]`);
            }
        }
    }
    /**
     * Initializes a new instance of the RouteStore
     * @param initialRoute Initial route descriptor to set
     * @param loggers Logger context
     * @param persistentStorage Persistent storage to use for route persistence
     * @param configProvider Configuration provider
     * @param persistRoutes Flag to indicate if routes should be persisted
     * @param preferCache Flag to indicate if cache should be preferred over local storage for hydration
     * @param routePathPrefix If specified, this is the prefix to use when forming the route path
     * @param defaultQueryParameters If specified, these are default query parameters when serializing route
     * @param persistableSubRouteTypes If specified, only these sub route types will have their cache persisted to storage
     */ constructor(initialRoute, loggers, persistentStorage, configProvider, persistRoutes, preferCache, routePathPrefix, defaultQueryParameters, persistableSubRouteTypes = null){
        super(), this.loggers = loggers, this.persistentStorage = persistentStorage, this.configProvider = configProvider, this.persistRoutes = persistRoutes, this.preferCache = preferCache, this.routePathPrefix = routePathPrefix, this.defaultQueryParameters = defaultQueryParameters, this.persistableSubRouteTypes = persistableSubRouteTypes;
        const shouldHydrateFromStorage = this.configProvider().settings.routePersistenceEnabled && this.preferCache;
        this.currentRoute = initialRoute;
        // Set the defaults for the current sub route
        this.currentRoute.subRoute = fillSubRouteDefaults(this.currentRoute.subRoute, this.currentRoute.subRouteType);
        // Persist the initial route if supported (helpful in case of deep-links)
        const didHydrateFromStorage = shouldHydrateFromStorage && this.currentRoute != null;
        if (!didHydrateFromStorage) {
            this.persistRoute();
        }
        this.subRouteCache = this.hydrateSubRouteCache();
        const path = this.formRoutePath(this.currentRoute);
        history.replaceState(null, "", path);
        setCurrentRouteInfoForLogs(this.currentRoute.subRouteType, this.currentRoute.subRoute, this.loggers.logHandler);
    }
}
/** Key to persist the current route in local storage */ RouteStore.RouteKey = "rstore-croute";
/** Key to persist the sub route cache in local storage */ RouteStore.SubRouteCacheKey = "rstore-srcache";
